import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { Howl } from "howler";
import { GAME_SOUNDS, SOUND_NAME } from "../../sounds";
import { soundManager } from "../../SoundManager";
import StorageManager from "../../utils/StorageManager";

export const HowlCache = {};
export const soundDurations = {};

export const loadSounds = createAsyncThunk(
  "sounds/loadSounds",
  async (_, { rejectWithValue }) => {
    // 🚀 PARALLEL LOADING - Load all sounds simultaneously
    const soundPromises = GAME_SOUNDS.sounds.map(async (sound) => {
      if (!HowlCache[sound.name]) {
        // Create Howl with sprite support
        const howlConfig = {
          src: [sound.url],
          loop: sound.loop || false,
          volume: sound.volume || 1.0,
        };

        // Add sprite configuration if present
        if (sound.sprite) {
          howlConfig.sprite = sound.sprite;
        }

        HowlCache[sound.name] = new Howl(howlConfig);

        try {
          await new Promise((resolve, reject) => {
            const howl = HowlCache[sound.name];

            // Add more comprehensive error handling
            howl.once("load", () => {
              soundDurations[sound.name] = howl.duration();

              // If this is a sprite, also store individual sprite durations
              if (sound.sprite) {
                Object.entries(sound.sprite).forEach(([spriteName, [start, duration]]) => {
                  soundDurations[spriteName] = duration / 1000; // Convert ms to seconds
                });
              }

              resolve();
            });

            howl.once("loaderror", (id, error) => {
              reject(
                new Error(`Failed to load sound: ${sound.name} - ${error}`)
              );
            });

            // Set a timeout to catch hanging loads
            setTimeout(() => {
              if (howl.state() === "loading") {
                reject(new Error(`Sound loading timeout: ${sound.name}`));
              }
            }, 10000); // 10 second timeout
          });
        } catch (error) {
          throw error; // Let Promise.all catch this
        }
      }
    });

    // Wait for all sounds to finish loading (happens in parallel!)
    try {
      await Promise.all(soundPromises);
    } catch (error) {
      return rejectWithValue(`Failed to load some sounds`);
    }

    return {
      soundKeys: Object.keys(HowlCache),
      durations: soundDurations,
    };
  }
);

// Load saved sound settings from localStorage
const savedSettings = StorageManager.loadSoundSettings();

const soundsSlice = createSlice({
  name: "sounds",
  initialState: {
    soundEffectsEnabled: savedSettings.soundEffectsEnabled,
    backgroundMusicEnabled: savedSettings.backgroundMusicEnabled,
    globalSoundDisabled: savedSettings.globalSoundDisabled,
    masterVolume: savedSettings.masterVolume,
    previousMasterVolume: savedSettings.masterVolume, // Initialize with saved volume
    previousSoundEffectsEnabled: savedSettings.soundEffectsEnabled, // Initialize with saved state
    previousBackgroundMusicEnabled: savedSettings.backgroundMusicEnabled, // Initialize with saved state
    currentBackgroundMusic: SOUND_NAME.background_music,
    soundKeys: [],
    soundVolumes: {},
    status: "idle",
    error: null,
  },
  reducers: {
    toggleSoundEffects(state) {
      state.soundEffectsEnabled = !state.soundEffectsEnabled;

      // If re-enabling, clear globalSoundDisabled (we have sound again)
      if (state.soundEffectsEnabled && state.globalSoundDisabled) {
        state.globalSoundDisabled = false;
        state.masterVolume = state.previousMasterVolume > 0 ? state.previousMasterVolume : 1.0;

        // Restore volume to all sounds
        Object.values(HowlCache).forEach((sound) => {
          sound.volume(state.masterVolume);
        });

        soundManager.toggleGlobal(false);
      }

      // Build list of music tracks to exclude from sound effects toggle
      const musicTracks = [
        SOUND_NAME.background_music,
        SOUND_NAME.free_spin_music,
        SOUND_NAME.feature_background_music,
      ];
      if (SOUND_NAME.ambient_music) {
        musicTracks.push(SOUND_NAME.ambient_music);
      }

      // Mute/unmute only non-music sounds
      Object.entries(HowlCache).forEach(([key, sound]) => {
        if (!musicTracks.includes(key)) {
          sound.mute(!state.soundEffectsEnabled);
        }
      });

      // Sync SoundManager
      soundManager.soundEffectsEnabled = state.soundEffectsEnabled;
      soundManager.globalSoundDisabled = state.globalSoundDisabled;

      // Save to localStorage
      StorageManager.saveSoundSettings({
        masterVolume: state.masterVolume,
        soundEffectsEnabled: state.soundEffectsEnabled,
        backgroundMusicEnabled: state.backgroundMusicEnabled,
        globalSoundDisabled: state.globalSoundDisabled,
      });
    },

    toggleBackgroundMusic(state) {
      state.backgroundMusicEnabled = !state.backgroundMusicEnabled;

      // If re-enabling, clear globalSoundDisabled (we have sound again)
      if (state.backgroundMusicEnabled && state.globalSoundDisabled) {
        state.globalSoundDisabled = false;
        state.masterVolume = state.previousMasterVolume > 0 ? state.previousMasterVolume : 1.0;

        // Restore volume to all sounds
        Object.values(HowlCache).forEach((sound) => {
          sound.volume(state.masterVolume);
        });

        soundManager.toggleGlobal(false);
      }

      // Handle music tracks
      const regularMusic = HowlCache[SOUND_NAME.background_music];
      const freeSpinMusic = HowlCache[SOUND_NAME.free_spin_music];
      const featureMusic = HowlCache[SOUND_NAME.feature_background_music];
      const ambientMusic = SOUND_NAME.ambient_music ? HowlCache[SOUND_NAME.ambient_music] : null;

      if (state.backgroundMusicEnabled) {
        // Play the current background music
        const currentMusic = HowlCache[state.currentBackgroundMusic];
        if (currentMusic) {
          currentMusic.mute(false);
          if (!currentMusic.playing()) currentMusic.play();
        }
        if (ambientMusic) {
          ambientMusic.mute(false);
          if (!ambientMusic.playing()) ambientMusic.play();
        }
      } else {
        // Stop all music tracks
        if (regularMusic) regularMusic.stop();
        if (freeSpinMusic) freeSpinMusic.stop();
        if (featureMusic) featureMusic.stop();
        if (ambientMusic) ambientMusic.stop();
      }

      // Sync SoundManager
      soundManager.backgroundMusicEnabled = state.backgroundMusicEnabled;
      soundManager.globalSoundDisabled = state.globalSoundDisabled;

      // Save to localStorage
      StorageManager.saveSoundSettings({
        masterVolume: state.masterVolume,
        soundEffectsEnabled: state.soundEffectsEnabled,
        backgroundMusicEnabled: state.backgroundMusicEnabled,
        globalSoundDisabled: state.globalSoundDisabled,
      });
    },

    toggleGlobalSound(state, action) {
      state.globalSoundDisabled = !state.globalSoundDisabled;

      // CRITICAL: Sync SoundManager FIRST before manipulating HowlCache
      // This ensures soundManager.play() checks use the correct state immediately
      soundManager.toggleGlobal(state.globalSoundDisabled);

      if (state.globalSoundDisabled) {
        // Store current states before disabling
        state.previousMasterVolume = state.masterVolume;
        state.previousSoundEffectsEnabled = state.soundEffectsEnabled;
        state.previousBackgroundMusicEnabled = state.backgroundMusicEnabled;

        // Set volume to 0
        state.masterVolume = 0;

        state.soundEffectsEnabled = false;
        state.backgroundMusicEnabled = false;

        // Apply volume 0 and stop all sounds
        Object.values(HowlCache).forEach((sound) => {
          sound.volume(0);
          sound.mute(true);
          if (sound.playing()) sound.stop();
        });
      } else {
        // When re-enabling from global mute, enable both checkboxes
        // This is the expected behavior when clicking the sound button to unmute
        state.soundEffectsEnabled = true;
        state.backgroundMusicEnabled = true;

        // Restore volume to previous value, or default to 1.0 if it was 0
        state.masterVolume = state.previousMasterVolume > 0 ? state.previousMasterVolume : 1.0;

        // Apply restored volume and unmute all sounds
        Object.values(HowlCache).forEach((sound) => {
          sound.volume(state.masterVolume);
          sound.mute(false); // Unmute everything since we're enabling both checkboxes
        });

        // Play music since we're enabling it
        if (state.backgroundMusicEnabled) {
          // Determine which music to play based on game state (passed from component)
          const { isInHoldAndWin } = action.payload || {};
          const musicToPlay = isInHoldAndWin
            ? SOUND_NAME.feature_background_music
            : SOUND_NAME.background_music;

          // Update current background music reference
          state.currentBackgroundMusic = musicToPlay;
          soundManager.currentBackgroundMusic = musicToPlay;

          // Play the appropriate music
          const currentMusic = HowlCache[musicToPlay];
          const ambientMusic = SOUND_NAME.ambient_music ? HowlCache[SOUND_NAME.ambient_music] : null;
          if (currentMusic) {
            currentMusic.play();
          }
          if (ambientMusic) {
            ambientMusic.play();
          }
        }
      }

      // Save to localStorage
      StorageManager.saveSoundSettings({
        masterVolume: state.masterVolume,
        soundEffectsEnabled: state.soundEffectsEnabled,
        backgroundMusicEnabled: state.backgroundMusicEnabled,
        globalSoundDisabled: state.globalSoundDisabled,
      });
    },

    setMasterVolume(state, action) {
      const newVolume = Math.max(0, Math.min(2, action.payload));
      const wasZero = state.masterVolume === 0;
      const isNowZero = newVolume === 0;

      // Moving TO zero - disable all sounds
      if (!wasZero && isNowZero) {
        // Store current checkbox states before disabling
        state.previousSoundEffectsEnabled = state.soundEffectsEnabled;
        state.previousBackgroundMusicEnabled = state.backgroundMusicEnabled;
        state.previousMasterVolume = state.masterVolume > 0 ? state.masterVolume : 1.0;

        // Disable everything
        state.globalSoundDisabled = true;
        state.soundEffectsEnabled = false;
        state.backgroundMusicEnabled = false;
        state.masterVolume = 0;

        // Mute and stop all sounds
        Object.values(HowlCache).forEach((sound) => {
          sound.volume(0);
          sound.mute(true);
          if (sound.playing()) sound.stop();
        });

        // Sync SoundManager
        soundManager.toggleGlobal(true);
        soundManager.soundEffectsEnabled = false;
        soundManager.backgroundMusicEnabled = false;
        soundManager.globalSoundDisabled = true;

        // Save to localStorage
        StorageManager.saveSoundSettings({
          masterVolume: state.masterVolume,
          soundEffectsEnabled: state.soundEffectsEnabled,
          backgroundMusicEnabled: state.backgroundMusicEnabled,
          globalSoundDisabled: state.globalSoundDisabled,
        });
        return;
      }

      // Moving FROM zero - restore previous checkbox states
      if (wasZero && !isNowZero) {
        state.masterVolume = newVolume;
        state.globalSoundDisabled = false;

        // Restore only the checkboxes that were active before
        state.soundEffectsEnabled = state.previousSoundEffectsEnabled;
        state.backgroundMusicEnabled = state.previousBackgroundMusicEnabled;

        // Apply volume and unmute all sounds
        Object.values(HowlCache).forEach((sound) => {
          sound.volume(state.masterVolume);
          sound.mute(false);
        });

        // If background music was enabled, play it
        if (state.backgroundMusicEnabled) {
          const currentMusic = HowlCache[state.currentBackgroundMusic];
          const ambientMusic = SOUND_NAME.ambient_music ? HowlCache[SOUND_NAME.ambient_music] : null;
          if (currentMusic && !currentMusic.playing()) {
            currentMusic.play();
          }
          if (ambientMusic && !ambientMusic.playing()) {
            ambientMusic.play();
          }
        }

        // Sync SoundManager
        soundManager.toggleGlobal(false);
        soundManager.soundEffectsEnabled = state.soundEffectsEnabled;
        soundManager.backgroundMusicEnabled = state.backgroundMusicEnabled;
        soundManager.globalSoundDisabled = false;

        // Save to localStorage
        StorageManager.saveSoundSettings({
          masterVolume: state.masterVolume,
          soundEffectsEnabled: state.soundEffectsEnabled,
          backgroundMusicEnabled: state.backgroundMusicEnabled,
          globalSoundDisabled: state.globalSoundDisabled,
        });
        return;
      }

      // Normal volume change (not crossing zero threshold)
      state.masterVolume = newVolume;

      // Apply volume to all sounds in HowlCache
      Object.values(HowlCache).forEach((sound) => {
        sound.volume(state.masterVolume);
      });

      // Save to localStorage
      StorageManager.saveSoundSettings({
        masterVolume: state.masterVolume,
        soundEffectsEnabled: state.soundEffectsEnabled,
        backgroundMusicEnabled: state.backgroundMusicEnabled,
        globalSoundDisabled: state.globalSoundDisabled,
      });
    },
  },

  extraReducers: (builder) => {
    builder
      .addCase(loadSounds.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(loadSounds.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.soundKeys = action.payload.soundKeys;
        state.soundDurations = {
          ...state.soundDurations,
          ...action.payload.durations,
        };

        // Apply saved settings to all loaded sounds
        if (state.globalSoundDisabled) {
          // Global sound disabled - mute everything and set volume to 0
          Object.values(HowlCache).forEach((sound) => {
            sound.volume(0);
            sound.mute(true);
          });
        } else {
          // Apply saved master volume
          Object.values(HowlCache).forEach((sound) => {
            sound.volume(state.masterVolume);
          });

          // Apply mute state based on individual settings
          Object.entries(HowlCache).forEach(([key, sound]) => {
            // Build list of music tracks
            const musicTracks = [
              SOUND_NAME.background_music,
              SOUND_NAME.feature_background_music,
            ];
            
            if (SOUND_NAME.ambient_music) {
              musicTracks.push(SOUND_NAME.ambient_music);
            }
            
            // Music sounds
            if (musicTracks.includes(key)) {
              sound.mute(!state.backgroundMusicEnabled);
            }
            // Sound effects (everything else)
            else {
              sound.mute(!state.soundEffectsEnabled);
            }
          });
        }
      })
      .addCase(loadSounds.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message;
      });
  },
});

export const {
  toggleSoundEffects,
  toggleBackgroundMusic,
  toggleGlobalSound,
  setMasterVolume,
} = soundsSlice.actions;

export const selectMasterVolume = (state) => state.sound.masterVolume;

export default soundsSlice.reducer;
